import { UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  convertCampaignDates,
  formatFormFields,
  getDropdownOptions,
  initialFieldLoad,
} from "../../CreateCampaign/Helpers/CampaignFunctions";
import { FieldType } from "../../DynamicGroup/DynamicGroup";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import {
  BaseFormField,
  BaseFormFieldWithHistory,
  ICampaign,
  ICampaignAPI,
  ICampaignFormState,
  ICreateCampaign,
  ICreateCampaignCommand,
  IDeleteCampaign,
  IDeleteCampaignCommand,
  IDuplicateCampaignCommand,
  IEditCampaign,
  IEditCampaignCommand,
  IFormConfigExport,
  IFormInfo,
  IFormQueryResponse,
  IFormResponse,
  IFormResponseList,
  IGetFilteredResponsesQuery,
  IMessageCampaign,
  IPaginationResponse,
  ISaveDripCampaignCommand,
  IUpsertCampaignReportMessage,
} from "../Typings/Campaigns";
import { IPagination } from "../Typings/MessageText";
import { SuccessResponse } from "../Typings/Queries";
import { apiPathCampaigns, fetcher, secureFetcher } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";

export const useSaveCampaign = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<IFormInfo, unknown, ICreateCampaign>(
    async (newCampaign) => {
      newCampaign.endDate = newCampaign.endDate ? newCampaign.endDate : new Date("12/31/2099");

      const createCampaignCommand: ICreateCampaignCommand = {
        ...newCampaign,
        clientId,
        userId,
      };

      const response = await fetcher(apiPathCampaigns + "create", {
        method: "POST",
        body: JSON.stringify(createCampaignCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["campaigns", clientId]),
      mutationKey: ["saveCampaign"],
    },
  );
};

export const useUploadForm = () => {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const { clientId, termsOfServiceUrl, privacyPolicyUrl } = useAppSelector(selectClientValue);

  return useMutation<IFormInfo, unknown, ICampaignFormState>(
    async (form) => {
      form.endDate = form.endDate ? form.endDate : new Date("12/31/2099");

      const options = {
        method: "POST",
        body: JSON.stringify({
          ...form,
          clientId,
          termsOfServiceUrl,
          privacyPolicyUrl,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      return await fetcher(apiPathCampaigns + "config/upload", options);
    },
    {
      mutationKey: ["formUpload"],
      onSuccess: async (_, variables) => {
        queryClient.invalidateQueries(["allClients"]);
        queryClient.invalidateQueries(["formData", variables.campaignId]);
        await queryClient.refetchQueries([
          "formData",
          { campaignId: variables.campaignId, formId: variables.formId },
        ]);
        await queryClient.refetchQueries(["campaign", variables.campaignId]);
        queryClient.invalidateQueries(["campaigns", clientId]);
      },
    },
  );
};

/**
 * Provides a way to select a subset of the Campaigns for the currently selected client and
 * share the query cache with other selector queries.
 * @param select - Transform or select a part of the data returned from the original query
 * @param key - The key to use for the new query
 * @typeParam SelectorReturnType - The type of the data returned from your select function
 */
export function useCampaignsQuerySelector<SelectorReturnType>(
  select?: (data: ICampaign[]) => SelectorReturnType | undefined,
  key?: string,
  disableRefetch?: boolean,
  includeDeleted?: boolean,
) {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<ICampaign[], unknown, SelectorReturnType | undefined>(
    ["campaigns", clientId, `${key}_${includeDeleted}`],
    async () => {
      const campaigns = (await secureFetcher(
        apiPathCampaigns + clientId + `?includeDeleted=${includeDeleted ?? false}`,
        token,
      )) as ICampaignAPI[];
      return campaigns.map(convertCampaignDates);
    },
    {
      select,
      enabled: !!clientId && !disableRefetch && !!token,
    },
  );
}

/**
 * Provides a way to select a subset of the Campaign data based on the provided campaign ID and
 * share the query cache with other selector queries.
 * @param select - Transform or select a part of the data returned from the original query
 * @param key - The key to use for the new query
 * @typeParam SelectorReturnType - The type of the data returned from your select function
 */
export function useCampaignQuerySelector<SelectorReturnType>(
  campaignId: string,
  select?: (data: ICampaign) => SelectorReturnType | undefined,
  enabled = true,
  key?: string,
) {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<ICampaign, unknown, SelectorReturnType | undefined>(
    ["campaign", campaignId, key],
    async () => {
      const campaign = (await secureFetcher(apiPathCampaigns + "id/" + campaignId, token)) as ICampaignAPI;
      return convertCampaignDates(campaign);
    },
    {
      select,
      enabled: enabled && !!clientId && !!campaignId && !!token,
    },
  );
}

export function useAllMessageCampaigns(page: number, pageSize: number, disableRefetch?: boolean) {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<IPagination<IMessageCampaign>, unknown, IPagination<IMessageCampaign>>(
    ["message_campaigns", clientId, page, pageSize],
    () => secureFetcher(`${apiPathCampaigns}message/${clientId}?page=${page}&pageSize=${pageSize}`, token),
    {
      enabled: !!clientId && !disableRefetch && !!token,
    },
  );
}

/**
 * Get all campaigns based on the selected client
 * @returns an array of Campaigns associated with the currently selected client
 */
export function useAllCampaigns(queryString: string) {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<IPagination<ICampaign>>(
    ["campaigns", clientId, queryString],
    async () => {
      const campaigns = (await secureFetcher(
        apiPathCampaigns + clientId + queryString + "&includeDeleted=false",
        token,
      )) as IPagination<ICampaignAPI>;
      return {
        ...campaigns,
        items: campaigns.items.map(convertCampaignDates),
      };
    },
    {
      enabled: !!clientId && !!token,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
}

/**
 * Get a single campaign based on the selected client
 * @returns a Campaign object
 */
export function useSingleCampaign(campaignId: string) {
  return useCampaignQuerySelector<ICampaign>(campaignId, undefined, undefined);
}

export function useFormDataQuery(campaignId: string, formId: string, action: string) {
  const token = useAuthToken();
  return useQuery<ICampaignFormState>(
    ["formData", campaignId],
    async () => {
      const response = (await secureFetcher(
        `${apiPathCampaigns}config/${campaignId}/${formId}`,
        token,
      )) as ICampaignFormState;
      if (response && action === "Duplicate") {
        response.campaignName = response.campaignName + " (Copy)";
      }
      response.totalCustomFields = Object.keys(response.formFields).filter((f) =>
        f.includes("custom"),
      ).length;

      response.formFields = initialFieldLoad(
        response.formFields,
        response.campaignType,
        response.contactPreference,
      );
      return response;
    },
    {
      enabled: !!campaignId && !!formId && !!token,
      cacheTime: 0,
    },
  );
}

export function useCampaignFormDataQuery<SelectorReturnType>(
  campaignId: string,
  formId?: string,
  select?: (data: ICampaignFormState) => SelectorReturnType | undefined,
  enabled = true,
) {
  const token = useAuthToken();
  return useQuery<ICampaignFormState, unknown, SelectorReturnType | undefined>(
    ["formData", { campaignId, formId }],
    async () => {
      let url = `${apiPathCampaigns}config/${campaignId}/${formId || "00000000-0000-0000-0000-000000000000"}`;
      const result = await secureFetcher(url, token);
      return {
        ...result,
        formFields: formatFormFields(result.formFields ?? {}, result.contactPreference),
      } as ICampaignFormState;
    },
    {
      select,
      enabled: !!campaignId && enabled && !!token,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
}

export function useCampaignFormFieldsQuery(campaignId: string, formId: string, queryEnabled = true) {
  return useCampaignFormDataQuery<BaseFormField[]>(
    campaignId,
    formId,
    (data) =>
      Object.keys(data.formFields).map((key) => ({
        id: key,
        ...data.formFields[key],
      })),
    queryEnabled,
  );
}
export function useCampaignFormFieldsWithLabelHistoryQuery(
  campaignId: string,
  formId: string,
  queryEnabled = true,
) {
  const { data: labelHistory = {}, isLoading } = useCampaignFormFieldLabelsQuery(
    campaignId,
    formId,
    queryEnabled,
  );
  return useCampaignFormDataQuery<BaseFormFieldWithHistory[]>(
    campaignId,
    formId,
    (data) =>
      Object.keys(data.formFields).map((key) => ({
        id: key,
        ...data.formFields[key],
        labelHistory: labelHistory[key] || new Set([data.formFields[key].label]),
      })),
    queryEnabled && !isLoading,
  );
}

export function useCampaignFormFieldLabelsQuery(campaignId: string, formId: string, queryEnabled = true) {
  return useCampaignFormDataQuery<Record<string, Set<string>>>(
    campaignId,
    formId,
    (data) => data.labelHistory,
    queryEnabled,
  );
}

export function useCampaignFormFieldFiltersQuery(
  campaignId: string,
  formId: string,
  storageKey = "campaignResponses",
  queryEnabled = true,
) {
  const { data, ...rest } = useCampaignFormFieldsWithLabelHistoryQuery(campaignId, formId, queryEnabled);
  let uniqueFields = [] as FieldType[];

  if (data !== undefined && data.length > 0) {
    uniqueFields = Array.from(
      new Map(
        data.map((field) => [
          `${field.id}_${field.label}`,
          {
            id: `campaignResponses.${storageKey}.${field.id}`,
            field: {
              ...field,
              fieldName: field.label,
              dropdownOptions: getDropdownOptions(field),
            },
          },
        ]),
      ).values(),
    );
  }

  return { data: uniqueFields, ...rest } as UseQueryResult<FieldType[]>;
}

export function useFormDataForClientQuery(clientId: string) {
  const token = useAuthToken();
  return useQuery<ICampaignFormState[]>(
    ["formDataForClient", clientId],
    async () => {
      const response = (await secureFetcher(
        `${apiPathCampaigns}config/${clientId}`,
        token,
      )) as ICampaignFormState[];

      response.forEach((r) => {
        r.totalCustomFields = Object.keys(r.formFields).filter((f) => f.includes("custom")).length;
        Object.keys(r.formFields).forEach((value, index) => {
          r.formFields[value].order = r.formFields[value].order ?? index;
        });
      });

      return response;
    },
    {
      enabled: !!clientId && !!token,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 0,
    },
  );
}

export const useSaveMessageCampaign = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<string, unknown, ISaveDripCampaignCommand>(
    (campaign) => {
      return fetcher(apiPathCampaigns + "message", {
        method: "POST",
        body: JSON.stringify(campaign),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(["message_campaigns", clientId]);
      },
      mutationKey: ["saveCampaign"],
    },
  );
};

export const useDuplicateCampaign = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<string, unknown, string>(
    (campaignId) => {
      const duplicateCommand: IDuplicateCampaignCommand = {
        campaignId,
        userId,
      };

      return fetcher(apiPathCampaigns + "duplicate", {
        method: "POST",
        body: JSON.stringify(duplicateCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["campaigns", clientId]),
      mutationKey: ["duplicateCampaign"],
    },
  );
};

export const useDeleteCampaign = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<boolean, unknown, IDeleteCampaign>(
    (deleteBody) => {
      const deleteCommand: IDeleteCampaignCommand = {
        ...deleteBody,
        userId,
      };

      return fetcher(apiPathCampaigns + "delete", {
        method: "PUT",
        body: JSON.stringify(deleteCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: (response, deleteBody) => {
        queryClient.removeQueries(["campaign", deleteBody.campaignId]);
        queryClient.removeQueries(["formData", deleteBody.campaignId]);
        queryClient.invalidateQueries(["message_campaigns", clientId]);
        queryClient.invalidateQueries(["groups"]);
        return queryClient.invalidateQueries(["campaigns", clientId]);
      },
      onError: (error) => {
        console.error(error);
      },
      mutationKey: ["deleteCampaign"],
    },
  );
};

export const useEditCampaign = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<boolean, unknown, IEditCampaign>(
    (updatedCampaign) => {
      updatedCampaign.endDate = updatedCampaign.endDate ? updatedCampaign.endDate : new Date("12/31/2099");

      const editCampaignCommand: IEditCampaignCommand = {
        ...updatedCampaign,
        userId,
      };
      return fetcher(apiPathCampaigns + "edit", {
        method: "PUT",
        body: JSON.stringify(editCampaignCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: async (response, editCampaignCommand) => {
        await queryClient.invalidateQueries(["campaign", editCampaignCommand.campaignId]);
        await queryClient.invalidateQueries(["formData", editCampaignCommand.campaignId]);
        return queryClient.invalidateQueries(["campaigns", clientId]);
      },
      onError: (error) => {
        console.error(error);
      },
      mutationKey: ["editCampaign"],
    },
  );
};

export const useUpsertCampaignReport = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, IUpsertCampaignReportMessage>(
    (updatedCampaign) =>
      fetcher(apiPathCampaigns + `${updatedCampaign.campaignId}/report`, {
        method: "POST",
        body: JSON.stringify(updatedCampaign),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: async (response, editCampaignCommand) => {
        queryClient.invalidateQueries(["campaign", editCampaignCommand.campaignId]);
      },
      onError: (error) => {
        console.error(error);
      },
      mutationKey: ["upsertCampaignReport"],
    },
  );
};

/**
 * Get just the responses from a single campaign based on the provided campaignId and formId
 * @returns an array of raw form data objects
 */
export const useGetFilteredResponses = (request: IGetFilteredResponsesQuery, queryEnabled = true) => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  // Remove default empty rules
  const rules = request.rules.filter(
    (r) =>
      !!r.filter.type &&
      r.filter.value !== "" &&
      (r.filter.value.type === "location" || r.filter.value.value !== ""),
  );

  const req = {
    ...request,
    clientId,
    rules: JSON.stringify(rules),
    formFields: request.formFields ? JSON.stringify(request.formFields) : null,
  };

  const enabled = !!clientId && !!req.campaignId && !!req.formId && queryEnabled;

  return useQuery<IPaginationResponse<IFormQueryResponse>>(
    ["campaignResponses", req],
    async () => {
      const result = (await secureFetcher(apiPathCampaigns + "responses", token, {
        body: JSON.stringify(req),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      })) as IPaginationResponse<IFormQueryResponse>;
      result.items.forEach((response) => {
        response.candidateResponse.candidateId = response.candidateId;
        response.candidateResponse.dateAdded = new Date(response.dateSubmitted);

        if (response.selectedJobAlerts && response.selectedJobAlerts.filter((j) => !!j.display).length > 0) {
          response.candidateResponse.interestPicker = response.selectedJobAlerts
            .map((j) => j.display)
            .join(", ");
        }
      });

      return result;
    },
    {
      enabled: enabled && !!token,
      staleTime: 30 * 1000, // 30 seconds
    },
  );
};

export const useDownloadFilteredResponses = () => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<boolean, unknown, IGetFilteredResponsesQuery>(
    async (request) => {
      const rules = request.rules.filter(
        (r) =>
          (!!r.filter.type && r.filter.value !== "" && r.filter.value.value !== "") ||
          r.filter.type === "location",
      );

      const req = {
        ...request,
        clientId,
        rules: JSON.stringify(rules),
        formFields: request.formFields ? JSON.stringify(request.formFields) : null,
        search: request.paginationRequest.search,
      };

      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_API}/api${apiPathCampaigns}responses/download`,
        {
          body: JSON.stringify(req),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const blob = await response.blob();
      const fileName = response.headers.get("Content-Disposition")?.split("filename=")[1];
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName ?? "responses.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    },
    {
      onSettled: (_, error) => {
        if (error) {
          console.error(error);
          return false;
        } else {
          return true;
        }
      },
    },
  );
};

export interface IExportFormConfigRequest {
  campaignId: string;
  formId: string;
  campaignName: string;
}

/**
 * Slugifies a campaign name for use in a downloaded file name
 */
const slugifyFileName = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "form";

/**
 * Downloads a form's full configuration (campaign details + form fields) as a JSON file,
 * so it can be imported to recreate the form in another environment
 */
export const useExportFormConfig = () => {
  const token = useAuthToken();

  return useMutation<boolean, unknown, IExportFormConfigRequest>(
    async ({ campaignId, formId, campaignName }) => {
      const formState = (await secureFetcher(
        `${apiPathCampaigns}config/${campaignId}/${formId}`,
        token,
      )) as ICampaignFormState;

      const exportPayload: IFormConfigExport = {
        exportVersion: 1,
        exportedAt: new Date().toISOString(),
        formState,
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${slugifyFileName(campaignName)}-form-config.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Deferred so the browser has started the download before the blob URL is revoked
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
      return true;
    },
    {
      mutationKey: ["exportFormConfig"],
      onError: (error) => {
        console.error("Error exporting form configuration", error);
      },
    },
  );
};

export const useExportResponseAttachments = () => {
  const token = useAuthToken();
  const { id: userId } = useCurrentUser();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation(
    (request: IGetFilteredResponsesQuery) => {
      const rules = request.rules.filter(
        (r) =>
          !!r.filter.type &&
          r.filter.value !== "" &&
          (r.filter.value.type === "location" || r.filter.value.value !== ""),
      );
      const req = {
        ...request,
        userId,
        clientId,
        search: request.paginationRequest.search,
        rules: JSON.stringify(rules),
        formFields: request.formFields ? JSON.stringify(request.formFields) : null,
      };
      const path = `${apiPathCampaigns}attachments/download`;
      return secureFetcher(path, token, {
        body: JSON.stringify(req),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }) as Promise<SuccessResponse>;
    },
    {
      onError: (error) => {
        console.error("Error exporting response attachments", error);
      },
      mutationKey: ["exportResponseAttachments", clientId],
    },
  );
};
